/**
 * Family Management Center — Phase 1 of the private family portal.
 *
 * Tabs:
 *   1. Dashboard        — aggregate cards (counts, upcoming events,
 *                         birthdays, anniversaries, memorial reminders,
 *                         latest photos/messages/announcements/new
 *                         members/pending invitations).
 *   2. Living Directory — filterable list of living members with
 *                         quick-view profile drawer.
 *   3. Calendar         — merged birthdays / anniversaries / memorials /
 *                         family events over the next 90 days (or a
 *                         specific month).
 *   4. Roles & Invites  — admin-only.  Lists tree roles and pending
 *                         invitations, lets admins send new invites.
 *
 * Route: /family/:treeId/manage
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Users, CalendarDays, ShieldCheck, ArrowLeft,
  Cake, Heart, Flower2, ImageIcon, MessageCircle, UserPlus, Mail,
  Loader2, Phone, MapPin, Briefcase, GraduationCap, Search,
  Baby, PartyPopper, ScrollText, BookOpen, Save, Church, Cross,
  Sparkles, Inbox, Bell, CheckCircle2, Clock, TreePine, Paperclip,
  ExternalLink, Trash2, Globe2, HandCoins, MessageSquare,
} from "lucide-react";
import FamilyNetworkGraph from "@/components/FamilyNetworkGraph";
import CrossFamilyMessageDrawer from "@/components/CrossFamilyMessageDrawer";
import {
  FamilyPhotosTab,
  MeetingMinutesTab,
  CharityTab,
  CommentsNotesTab,
} from "@/components/FamilyManagementTabs";

const TABS = [
  { key: "dashboard",   label: "Dashboard",       Icon: LayoutDashboard },
  { key: "members",     label: "Living Members",  Icon: Users },
  { key: "documents",   label: "Documents",       Icon: BookOpen },
  { key: "photos",      label: "Family Photos",   Icon: ImageIcon },
  { key: "meetings",    label: "Meeting Minutes", Icon: ScrollText },
  { key: "charity",     label: "Charity",         Icon: HandCoins },
  { key: "notes",       label: "Comments",        Icon: MessageSquare },
  { key: "discoveries", label: "Discoveries",     Icon: Sparkles },
  { key: "messages",    label: "Messages",        Icon: Inbox, showBadge: true },
  { key: "memorial",    label: "Memorial Records",Icon: Flower2 },
  { key: "calendar",    label: "Calendar",        Icon: CalendarDays },
  { key: "roles",       label: "Roles & Invites", Icon: ShieldCheck, adminOnly: true },
];

const CALENDAR_ICON = {
  birthday:          Cake,
  birth_remembrance: Cake,
  anniversary:       Heart,
  memorial_day:      Flower2,
  event:             PartyPopper,
};
const CALENDAR_COLOR = {
  birthday:          "#DB2777",
  birth_remembrance: "#7C3AED",
  anniversary:       "#DC2626",
  memorial_day:      "#4338CA",
  event:             "#0891B2",
};

function StatCard({ icon: Icon, label, value, tone = "#4338CA" }) {
  return (
    <div
      className="p-4 rounded-xl border"
      style={{ borderColor: `${tone}33`, background: `${tone}0D` }}
      data-testid={`fmc-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ background: tone, color: "white" }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-2xl font-display font-semibold leading-tight">{value ?? "—"}</div>
          <div className="text-xs opacity-70">{label}</div>
        </div>
      </div>
    </div>
  );
}

function EmptyRow({ text }) {
  return <div className="py-4 text-sm opacity-60 text-center">{text}</div>;
}

function DashboardTab({ tree, data, loading }) {
  const [msgEdge, setMsgEdge] = useState(null);   // { edgeId, fromName, toName }
  if (loading || !data) {
    return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-50" /></div>;
  }
  const c = data.counts || {};
  return (
    <div className="space-y-6" data-testid="fmc-dashboard">
      {/* Hero */}
      <div
        className="p-6 rounded-2xl border"
        style={{
          borderColor: "#4338CA33",
          background: "linear-gradient(135deg, #EEF2FF 0%, #FDF4FF 100%)",
        }}
      >
        <div className="flex items-center gap-4 flex-wrap">
          {data.tree?.crest ? (
            <img src={resolveImage(data.tree.crest)} alt="" className="w-16 h-16 rounded-full object-cover border" />
          ) : (
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "#4338CA22" }}>
              <Users className="w-8 h-8" style={{ color: "#4338CA" }} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-xs font-mono uppercase tracking-widest opacity-60 flex items-center gap-2">
              FAMILY
              {data.tree?.fam_id && (
                <span
                  className="px-1.5 py-0.5 rounded text-[10px]"
                  style={{ background: "#4338CA22", color: "#4338CA" }}
                  data-testid="fmc-fam-id"
                >
                  {data.tree.fam_id}
                </span>
              )}
            </div>
            <h1 className="font-display text-3xl" data-testid="fmc-family-name">{data.tree?.name}</h1>
            {data.tree?.motto && <div className="text-sm italic opacity-80 mt-1">"{data.tree.motto}"</div>}
          </div>
          <Link
            to={`/family/${data.tree?.id}/mixer`}
            className="wofy-btn-primary text-sm flex items-center gap-1.5 shrink-0"
            style={{ background: "linear-gradient(135deg, #DB2777 0%, #7C3AED 100%)" }}
            data-testid="fmc-open-mixer"
          >
            <PartyPopper className="w-4 h-4" /> Open Reunion Mixer
          </Link>
        </div>
        {data.tree?.description && <p className="mt-4 text-sm opacity-80 whitespace-pre-wrap">{data.tree.description}</p>}
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Users}   label="Living Members"    value={c.living}    tone="#0891B2" />
        <StatCard icon={Flower2} label="In Memoriam"       value={c.deceased}  tone="#7C3AED" />
        <StatCard icon={Baby}    label="Family Branches"   value={c.branches}  tone="#DB2777" />
        <StatCard icon={ScrollText} label="Total in Tree"  value={c.total}     tone="#4338CA" />
      </div>

      {/* Related Families (cross-family network) */}
      {(data.related_families || []).length > 0 && (
        <div
          className="p-4 rounded-2xl border"
          style={{
            borderColor: "#0891B233",
            background: "linear-gradient(135deg, #ECFEFF 0%, #FFF7ED 100%)",
          }}
          data-testid="fmc-related-families"
        >
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4" style={{ color: "#0891B2" }} />
            <h3 className="font-display text-lg">Related Families</h3>
            <span className="text-xs opacity-60">— linked through marriage</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {(data.related_families || []).slice(0, 6).map((rf) => (
              <div
                key={rf.id}
                className="flex items-center gap-3 p-3 rounded-xl border bg-white"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`fmc-related-tree-${rf.id}`}
              >
                {rf.crest
                  ? <img src={resolveImage(rf.crest)} alt="" className="w-9 h-9 rounded-full object-cover" />
                  : <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#0891B21A" }}>
                      <Users className="w-4 h-4" style={{ color: "#0891B2" }} />
                    </div>}
                <Link to={`/family/${rf.id}/manage`} className="min-w-0 flex-1 hover:opacity-80">
                  <div className="text-sm font-semibold truncate">{rf.name}</div>
                  {rf.fam_id && <div className="text-[10px] font-mono opacity-60">{rf.fam_id}</div>}
                </Link>
                {rf.edge_id && (
                  <button
                    onClick={() => setMsgEdge({ edgeId: rf.edge_id, fromName: data.tree?.name, toName: rf.name })}
                    className="text-xs px-2 py-1 rounded-lg border shrink-0 hover:bg-white transition flex items-center gap-1"
                    style={{ borderColor: "#0891B255", color: "#0891B2" }}
                    data-testid={`fmc-message-family-${rf.id}`}
                    title={`Message the ${rf.name} family`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Force-directed-ish network graph — only render when there are
          any related families to show; otherwise a lone dot is boring. */}
      {(data.related_families || []).length > 0 && (
        <FamilyNetworkGraph treeId={data.tree?.id} className="wofy-card" />
      )}

      {/* Two-column grid */}
      <div className="grid lg:grid-cols-2 gap-5">
        <Panel title="Upcoming Events" icon={PartyPopper} testid="fmc-upcoming-events">
          {(data.upcoming_events || []).length === 0
            ? <EmptyRow text="No upcoming events. Add one from the Events tab." />
            : (data.upcoming_events || []).slice(0, 6).map((e) => (
                <div key={e.id} className="py-2 border-b last:border-0 flex items-start gap-2" style={{ borderColor: "var(--wofy-border)" }}>
                  <div className="text-xs mt-0.5 opacity-60 shrink-0 w-20">{e.event_date}</div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{e.title}</div>
                    {e.location && <div className="text-xs opacity-70 flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</div>}
                  </div>
                </div>
              ))}
        </Panel>

        <Panel title="Recent Birthdays" icon={Cake} testid="fmc-birthdays">
          {(data.recent_birthdays || []).length === 0
            ? <EmptyRow text="No birthdays in the next 30 days." />
            : (data.recent_birthdays || []).map((b) => (
                <div key={b.member_id} className="py-2 border-b last:border-0 flex items-center gap-3" style={{ borderColor: "var(--wofy-border)" }}>
                  {b.photo
                    ? <img src={resolveImage(b.photo)} alt="" className="w-9 h-9 rounded-full object-cover" />
                    : <div className="w-9 h-9 rounded-full" style={{ background: "#DB27771A" }} />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{b.name}</div>
                    <div className="text-xs opacity-70">{b.date} · in {b.days_until} day{b.days_until === 1 ? "" : "s"}</div>
                  </div>
                </div>
              ))}
        </Panel>

        <Panel title="Anniversaries" icon={Heart} testid="fmc-anniversaries">
          {(data.anniversaries || []).length === 0
            ? <EmptyRow text="No wedding anniversaries in the next 30 days." />
            : (data.anniversaries || []).map((a, i) => (
                <div key={i} className="py-2 border-b last:border-0" style={{ borderColor: "var(--wofy-border)" }}>
                  <div className="text-sm font-semibold">{a.name} &amp; {a.spouse_name || "spouse"}</div>
                  <div className="text-xs opacity-70">{a.date} · in {a.days_until} day{a.days_until === 1 ? "" : "s"}</div>
                </div>
              ))}
        </Panel>

        <Panel title="Memorial Reminders" icon={Flower2} testid="fmc-memorials">
          {(data.memorial_reminders || []).length === 0
            ? <EmptyRow text="No death anniversaries in the next 30 days." />
            : (data.memorial_reminders || []).map((m) => (
                <div key={m.member_id} className="py-2 border-b last:border-0 flex items-center gap-3" style={{ borderColor: "var(--wofy-border)" }}>
                  {m.photo
                    ? <img src={resolveImage(m.photo)} alt="" className="w-9 h-9 rounded-full object-cover grayscale" />
                    : <div className="w-9 h-9 rounded-full" style={{ background: "#7C3AED1A" }} />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{m.name}</div>
                    <div className="text-xs opacity-70">{m.date} · in {m.days_until} day{m.days_until === 1 ? "" : "s"}</div>
                  </div>
                  {m.memorial_slug && (
                    <Link to={`/celebrate-a-life/${m.memorial_slug}`} className="text-xs underline shrink-0" style={{ color: "#4338CA" }}>
                      Visit
                    </Link>
                  )}
                </div>
              ))}
        </Panel>

        <Panel title="Latest Photos" icon={ImageIcon} testid="fmc-photos">
          {(data.latest_photos || []).length === 0
            ? <EmptyRow text="No photos in family albums yet." />
            : (
              <div className="grid grid-cols-4 gap-2">
                {(data.latest_photos || []).slice(0, 8).map((p) => (
                  <div key={p.photo_id} className="aspect-square rounded-lg overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
                    <img src={resolveImage(p.image)} alt={p.caption || ""} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
        </Panel>

        <Panel title="Latest Messages" icon={MessageCircle} testid="fmc-messages">
          {(data.latest_messages || []).length === 0
            ? <EmptyRow text="No messages posted yet." />
            : (data.latest_messages || []).map((m) => (
                <div key={m.id} className="py-2 border-b last:border-0" style={{ borderColor: "var(--wofy-border)" }}>
                  <div className="text-xs opacity-60">{(m.author_name || "Anonymous")} · {(m.created_at || "").slice(0, 10)}</div>
                  <div className="text-sm mt-0.5 line-clamp-2">{m.body}</div>
                </div>
              ))}
        </Panel>

        <Panel title="New Family Members" icon={UserPlus} testid="fmc-new-members">
          {(data.new_members || []).length === 0
            ? <EmptyRow text="Nobody has been added in the last 30 days." />
            : (data.new_members || []).map((n) => (
                <div key={n.id} className="py-2 border-b last:border-0 flex items-center gap-3" style={{ borderColor: "var(--wofy-border)" }}>
                  {n.photo
                    ? <img src={resolveImage(n.photo)} alt="" className="w-8 h-8 rounded-full object-cover" />
                    : <div className="w-8 h-8 rounded-full" style={{ background: "#4338CA1A" }} />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{n.name}</div>
                    <div className="text-xs opacity-70">Added {(n.created_at || "").slice(0, 10)}</div>
                  </div>
                </div>
              ))}
        </Panel>

        <Panel title="Pending Invitations" icon={Mail} testid="fmc-invitations">
          {(data.pending_invitations || []).length === 0
            ? <EmptyRow text="No pending invitations." />
            : (data.pending_invitations || []).map((inv) => (
                <div key={inv.id || inv.email} className="py-2 border-b last:border-0 flex items-center justify-between" style={{ borderColor: "var(--wofy-border)" }}>
                  <div className="text-sm">{inv.email}</div>
                  <span className="text-xs uppercase font-mono px-2 py-0.5 rounded" style={{ background: "#EEF2FF", color: "#4338CA" }}>
                    {inv.role || "viewer"}
                  </span>
                </div>
              ))}
        </Panel>
      </div>

      {msgEdge && (
        <CrossFamilyMessageDrawer
          edgeId={msgEdge.edgeId}
          fromTreeName={msgEdge.fromName}
          toTreeName={msgEdge.toName}
          onClose={() => setMsgEdge(null)}
        />
      )}
    </div>
  );
}

function Panel({ title, icon: Icon, testid, children }) {
  return (
    <div className="wofy-card" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: "#4338CA" }} />
        <h3 className="font-display text-lg">{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}


function MembersTab({ treeId }) {
  const [members, setMembers] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    http.get(`/family-trees/${treeId}/members`)
      .then((r) => setMembers((r.data.items || []).filter((m) => (m.current_status || "living").toLowerCase() !== "deceased")))
      .catch(() => setMembers([]));
  }, [treeId]);

  const filtered = useMemo(() => {
    if (!members) return null;
    const s = q.trim().toLowerCase();
    if (!s) return members;
    return members.filter((m) => {
      const hay = [
        m.first_name, m.middle_name, m.last_name, m.nickname,
        (m.contact || {}).current_city, (m.contact || {}).country,
        m.occupation,
        ((m.genealogy || {}).branch),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [members, q]);

  if (members === null) {
    return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto opacity-50" /></div>;
  }

  return (
    <div data-testid="fmc-members">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            data-testid="fmc-members-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, city, occupation, branch…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
          />
        </div>
        <div className="text-sm opacity-70">{filtered.length} living member{filtered.length === 1 ? "" : "s"}</div>
      </div>

      {filtered.length === 0 ? (
        <div className="wofy-card text-center py-10 opacity-70">No living members match.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((m) => (
            <button
              key={m.id}
              onClick={() => setSelected(m)}
              className="text-left p-4 rounded-xl border hover:shadow-md transition"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid={`fmc-member-${m.id}`}
            >
              <div className="flex items-center gap-3">
                {m.photo
                  ? <img src={resolveImage(m.photo)} alt="" className="w-12 h-12 rounded-full object-cover" />
                  : <div className="w-12 h-12 rounded-full" style={{ background: "#4338CA1A" }} />}
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">
                    {[m.first_name, m.last_name].filter(Boolean).join(" ") || "Unnamed"}
                  </div>
                  {m.nickname && <div className="text-xs opacity-70 truncate">"{m.nickname}"</div>}
                  {m.sp_id && (
                    <div className="text-[10px] font-mono opacity-50 mt-0.5" data-testid={`fmc-sp-id-${m.id}`}>
                      {m.sp_id}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-2 text-xs opacity-70 space-y-0.5">
                {m.occupation && <div className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{m.occupation}</div>}
                {(m.contact || {}).current_city && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{(m.contact || {}).current_city}{(m.contact || {}).country ? `, ${(m.contact || {}).country}` : ""}</div>}
                {((m.genealogy || {}).branch) && <div className="text-[10px] uppercase tracking-widest mt-1 inline-block px-1.5 py-0.5 rounded" style={{ background: "#EEF2FF", color: "#4338CA" }}>{((m.genealogy || {}).branch)}</div>}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && <MemberDrawer member={selected} onClose={() => setSelected(null)} treeId={treeId} />}
    </div>
  );
}


function MemberDrawer({ member, onClose, treeId }) {
  const contact = member.contact || {};
  const emergency = member.emergency_contact || {};
  const langs = member.languages || {};
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="fmc-member-drawer"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            {member.photo
              ? <img src={resolveImage(member.photo)} alt="" className="w-20 h-20 rounded-full object-cover border" />
              : <div className="w-20 h-20 rounded-full" style={{ background: "#4338CA1A" }} />}
            <div className="flex-1 min-w-0">
              <h2 className="font-display text-2xl truncate">
                {[member.first_name, member.middle_name, member.last_name].filter(Boolean).join(" ")}
              </h2>
              {member.nickname && <div className="text-sm opacity-70">"{member.nickname}"</div>}
              {member.occupation && <div className="text-sm opacity-80 mt-1 flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{member.occupation}</div>}
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
            <Field label="Date of Birth" value={member.birth_date} />
            <Field label="Family Branch" value={(member.genealogy || {}).branch} />
            <Field label="Current City" value={[contact.current_city, contact.state, contact.country].filter(Boolean).join(", ")} icon={MapPin} />
            <Field label="Phone" value={contact.phone} icon={Phone} />
            <Field label="Email" value={contact.email} icon={Mail} />
            <Field label="Availability" value={member.availability} />
          </div>

          {(member.education || []).length > 0 && (
            <Section title="Education" icon={GraduationCap}>
              <ul className="text-sm space-y-1">
                {member.education.map((e, i) => (
                  <li key={i}>{[e.degree, e.name, e.graduation_year].filter(Boolean).join(" · ")}</li>
                ))}
              </ul>
            </Section>
          )}

          {(member.skills || []).length > 0 && (
            <Section title="Skills">
              <div className="flex flex-wrap gap-1.5">
                {member.skills.map((s, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded-full" style={{ background: "#EEF2FF", color: "#4338CA" }}>{s}</span>
                ))}
              </div>
            </Section>
          )}

          {((langs.native || []).length + (langs.other || []).length) > 0 && (
            <Section title="Languages">
              <div className="text-sm">
                {(langs.native || []).length > 0 && <div><strong>Native:</strong> {(langs.native || []).join(", ")}</div>}
                {(langs.other || []).length > 0 && <div><strong>Other:</strong> {(langs.other || []).join(", ")}</div>}
              </div>
            </Section>
          )}

          {(emergency.name || emergency.phone) && (
            <Section title="Emergency Contact">
              <div className="text-sm">
                <div><strong>{emergency.name}</strong>{emergency.relationship ? ` (${emergency.relationship})` : ""}</div>
                {emergency.phone && <div className="opacity-70">{emergency.phone}</div>}
                {emergency.email && <div className="opacity-70">{emergency.email}</div>}
              </div>
            </Section>
          )}

          {member.bio && (
            <Section title="Biography">
              <p className="text-sm whitespace-pre-wrap opacity-90">{member.bio}</p>
            </Section>
          )}

          <div className="mt-6 flex items-center justify-end gap-2">
            <Link to={`/family/${treeId}/member/${member.id}`} className="wofy-btn-secondary text-sm">View full profile</Link>
            <button onClick={onClose} className="wofy-btn-primary text-sm" data-testid="fmc-member-drawer-close">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest opacity-60">{label}</div>
      <div className="text-sm mt-0.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 opacity-60" />}
        <span>{value || <span className="opacity-40">—</span>}</span>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="mt-5">
      <div className="flex items-center gap-1.5 mb-1.5 text-sm font-semibold">
        {Icon && <Icon className="w-3.5 h-3.5 opacity-70" />}
        {title}
      </div>
      {children}
    </div>
  );
}


function CalendarTab({ treeId }) {
  const [data, setData] = useState(null);
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");

  const fetchIt = () => {
    const qs = year && month ? `?year=${year}&month=${month}` : "";
    http.get(`/family-trees/${treeId}/calendar${qs}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ entries: [] }));
  };
  useEffect(fetchIt, [treeId]); // eslint-disable-line

  const byMonth = useMemo(() => {
    if (!data?.entries) return {};
    const grouped = {};
    for (const e of data.entries) {
      const key = e.date.slice(0, 7); // YYYY-MM
      (grouped[key] = grouped[key] || []).push(e);
    }
    return grouped;
  }, [data]);

  if (data === null) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto opacity-50" /></div>;

  return (
    <div data-testid="fmc-calendar">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select value={year} onChange={(e) => setYear(e.target.value)} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} data-testid="fmc-cal-year">
          <option value="">Next 90 days</option>
          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i - 1).map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} disabled={!year} data-testid="fmc-cal-month">
          <option value="">Choose month</option>
          {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
            <option key={i + 1} value={i + 1}>{m}</option>
          ))}
        </select>
        <button onClick={fetchIt} className="wofy-btn-primary text-sm" data-testid="fmc-cal-go">Show</button>
        <div className="text-sm opacity-70 ml-auto">{data.count} entries · {data.range?.start} → {data.range?.end}</div>
      </div>

      {data.count === 0
        ? <div className="wofy-card text-center py-10 opacity-70">Nothing scheduled in this range.</div>
        : Object.entries(byMonth).map(([mk, entries]) => (
            <div key={mk} className="mb-6">
              <h3 className="font-display text-lg mb-2">{new Date(`${mk}-01`).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</h3>
              <div className="space-y-2">
                {entries.map((e, i) => {
                  const Icon = CALENDAR_ICON[e.kind] || PartyPopper;
                  const color = CALENDAR_COLOR[e.kind] || "#4338CA";
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid={`fmc-cal-entry-${e.kind}`}>
                      <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${color}1A`, color }}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="text-xs opacity-60 shrink-0 w-16">{e.date}</div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{e.title}</div>
                        {e.location && <div className="text-xs opacity-70 truncate">{e.location}{e.event_time ? ` · ${e.event_time}` : ""}</div>}
                      </div>
                      {e.memorial_slug && (
                        <Link to={`/celebrate-a-life/${e.memorial_slug}`} className="text-xs underline shrink-0" style={{ color: "#4338CA" }}>
                          Memorial
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
    </div>
  );
}


function RolesTab({ tree, treeId }) {
  const [roles, setRoles] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [sending, setSending] = useState(false);

  const load = () => {
    http.get(`/family-trees/${treeId}/roles`).then((r) => setRoles(r.data)).catch(() => setRoles({ items: [] }));
  };
  useEffect(load, [treeId]); // eslint-disable-line

  const invite = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      await http.post(`/family-trees/${treeId}/invitations`, { email: email.trim(), role });
      toast.success(`Invitation sent to ${email}`);
      setEmail("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invitation");
    } finally {
      setSending(false);
    }
  };

  if (roles === null) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto opacity-50" /></div>;

  return (
    <div className="space-y-6" data-testid="fmc-roles">
      <Panel title="Send an Invitation" icon={UserPlus} testid="fmc-invite-panel">
        <div className="grid sm:grid-cols-[1fr_140px_120px] gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="cousin@example.com"
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="fmc-invite-email"
          />
          <select value={role} onChange={(e) => setRole(e.target.value)} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wofy-border)" }} data-testid="fmc-invite-role">
            <option value="viewer">View-only</option>
            <option value="contributor">Contributor</option>
            <option value="editor">Editor</option>
            <option value="admin">Family Admin</option>
          </select>
          <button onClick={invite} disabled={sending || !email.trim()} className="wofy-btn-primary text-sm" data-testid="fmc-invite-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send invite"}
          </button>
        </div>
        <div className="mt-2 text-xs opacity-60">
          <strong>View-only</strong> = read the tree · <strong>Contributor</strong> = add stories/photos ·
          <strong> Editor</strong> = add/edit members &amp; events · <strong>Family Admin</strong> = full control incl. roles &amp; invites.
        </div>
      </Panel>

      <BulkInvitePanel treeId={treeId} onSent={load} />

      <Panel title="Current Roles" icon={ShieldCheck} testid="fmc-roles-list">
        {(roles.items || []).length === 0
          ? <EmptyRow text="No one else has been granted a role yet." />
          : (
            <div className="space-y-2">
              {roles.items.map((r) => (
                <div key={r.user_id} className="flex items-center gap-3 py-2 border-b last:border-0" style={{ borderColor: "var(--wofy-border)" }}>
                  {r.photo
                    ? <img src={resolveImage(r.photo)} alt="" className="w-9 h-9 rounded-full object-cover" />
                    : <div className="w-9 h-9 rounded-full" style={{ background: "#4338CA1A" }} />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{r.name || r.email || "Anonymous"}</div>
                    {r.email && <div className="text-xs opacity-60 truncate">{r.email}</div>}
                  </div>
                  <span className="text-xs uppercase font-mono px-2 py-0.5 rounded" style={{ background: "#EEF2FF", color: "#4338CA" }}>
                    {r.role || "member"}
                  </span>
                </div>
              ))}
            </div>
          )}
      </Panel>

      <DuplicateSuggestionsPanel treeId={treeId} />

      <DiasporaPassportPanel tree={tree} />
    </div>
  );
}


/** DiasporaPassportPanel — pick which sibling sites this tree appears
 *  on. Members can also opt-in to sibling exposure at the individual
 *  level from their own profile screen. Persists via PATCH
 *  /family-trees/:id/passport. */
const SIBLING_SITE_OPTIONS = [
  { slug: "socialpulse",       label: "Social Pulse",         hint: "Home platform (default)" },
  { slug: "indianfamilytree",  label: "IndianFamilyTree.com", hint: "Ancestor discovery sibling" },
  { slug: "kerala",            label: "Kerala.com",           hint: "For Kerala-rooted families" },
  { slug: "foodpulse",         label: "FoodPulse.net",        hint: "Cuisine & food heritage" },
  { slug: "indias",            label: "Indias.com",           hint: "Pan-Indian directory" },
  { slug: "keralatravels",     label: "KeralaTravels.com",    hint: "Travel & diaspora returnees" },
];

function DiasporaPassportPanel({ tree }) {
  const initial = Array.isArray(tree?.site_source)
    ? tree.site_source
    : (tree?.site_source ? [tree.site_source] : ["socialpulse"]);
  const [selected, setSelected] = useState(new Set(initial));
  const [saving, setSaving] = useState(false);

  const toggle = (slug) => {
    const next = new Set(selected);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    if (next.size === 0) next.add("socialpulse"); // never fully opt out of home site
    setSelected(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await http.patch(`/family-trees/${tree.id}/passport`, {
        site_sources: Array.from(selected),
      });
      toast.success("Diaspora Passport updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update passport");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title="Diaspora Passport" icon={Globe2} testid="fmc-passport-panel">
      <p className="text-xs opacity-70 mb-3">
        Choose which sibling sites this tree appears on. Family members in
        different diaspora communities can find your tree on the sites
        they use most.
      </p>
      <div className="grid sm:grid-cols-2 gap-2">
        {SIBLING_SITE_OPTIONS.map((s) => {
          const on = selected.has(s.slug);
          return (
            <label
              key={s.slug}
              className="flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer transition"
              style={{
                borderColor: on ? "#1D4ED8" : "var(--wofy-border)",
                background:  on ? "#EFF6FF" : "transparent",
              }}
              data-testid={`fmc-passport-opt-${s.slug}`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(s.slug)}
                className="mt-0.5"
                data-testid={`fmc-passport-checkbox-${s.slug}`}
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">{s.label}</span>
                <span className="block text-[11px] opacity-60">{s.hint}</span>
              </span>
            </label>
          );
        })}
      </div>
      <div className="mt-3 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="wofy-btn-primary text-sm"
          data-testid="fmc-passport-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save passport"}
        </button>
      </div>
    </Panel>
  );
}


/** BulkInvitePanel — paste up to 200 emails, pick a sender identity,
 *  fire them all as one batch. Uses the P1 bulk-invite endpoint. */
function BulkInvitePanel({ treeId, onSent }) {
  const [emailsRaw, setEmailsRaw] = useState("");
  const [role, setRole] = useState("viewer");
  const [sender, setSender] = useState("");
  const [senderName, setSenderName] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const parsedEmails = useMemo(() => {
    const raw = emailsRaw.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    return [...new Set(raw)].filter((e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
  }, [emailsRaw]);

  const send = async () => {
    if (parsedEmails.length === 0) {
      toast.error("Add at least one valid email address.");
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const r = await http.post(`/family-trees/${treeId}/invitations/bulk`, {
        emails: parsedEmails,
        role,
        sender_email: sender || null,
        sender_name:  senderName || null,
        note: note || null,
      });
      setResult(r.data);
      toast.success(`Sent ${r.data.sent_count} invitations (skipped ${r.data.skipped_count})`);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk invite failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Panel title="Bulk Invite" icon={UserPlus} testid="fmc-bulk-invite-panel">
      <div className="text-xs opacity-70 mb-2">
        Paste a list of email addresses (separated by commas, spaces, or newlines) and send them
        the same invitation in one go — perfect for onboarding a whole branch of the family at once.
      </div>
      <textarea
        rows={4}
        value={emailsRaw}
        onChange={(e) => setEmailsRaw(e.target.value)}
        placeholder="tom@family.com, siby@family.com&#10;biju@family.com"
        className="w-full px-3 py-2 rounded-lg border text-sm font-mono"
        style={{ borderColor: "var(--wofy-border)" }}
        data-testid="fmc-bulk-invite-emails"
      />
      <div className="mt-2 text-xs opacity-60" data-testid="fmc-bulk-invite-count">
        {parsedEmails.length} valid unique email{parsedEmails.length === 1 ? "" : "s"} detected.
      </div>

      <div className="grid sm:grid-cols-3 gap-2 mt-3">
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="fmc-bulk-invite-role"
        >
          <option value="viewer">View-only</option>
          <option value="editor">Editor</option>
          <option value="admin">Family Admin</option>
        </select>
        <select
          value={sender}
          onChange={(e) => setSender(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="fmc-bulk-invite-sender"
        >
          <option value="">Default sender (reply-to your account)</option>
          <option value="tomvell@gmail.com">Tom Vellaringattu — tomvell@gmail.com</option>
          <option value="sibyvell@gmail.com">Siby Vellaringattu — sibyvell@gmail.com</option>
          <option value="bijujoe@gmail.com">Biju Joe — bijujoe@gmail.com</option>
        </select>
        <input
          type="text"
          value={senderName}
          onChange={(e) => setSenderName(e.target.value)}
          placeholder="Sender display name (optional)"
          className="px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="fmc-bulk-invite-sender-name"
        />
      </div>

      <textarea
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional note included in every invitation email"
        className="w-full px-3 py-2 rounded-lg border text-sm mt-3"
        style={{ borderColor: "var(--wofy-border)" }}
        data-testid="fmc-bulk-invite-note"
      />

      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={send}
          disabled={sending || parsedEmails.length === 0}
          className="wofy-btn-primary text-sm"
          data-testid="fmc-bulk-invite-submit"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send ${parsedEmails.length || ""} invitations`}
        </button>
      </div>

      {result && (
        <div className="mt-4 text-xs" data-testid="fmc-bulk-invite-result">
          <div className="font-semibold mb-1">
            Sent: {result.sent_count} · Skipped: {result.skipped_count}
          </div>
          {(result.skipped || []).length > 0 && (
            <details>
              <summary className="cursor-pointer opacity-70">Show skipped</summary>
              <ul className="mt-1 space-y-0.5 opacity-70">
                {result.skipped.map((s, i) => (
                  <li key={i}>{s.email} — {s.reason}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </Panel>
  );
}




/** Duplicate-suggestion widget — surfaces fuzzy person matches that the
 * exact-match auto-merger didn't collapse.  Admin can visit each
 * person's profile to compare before requesting a merge. */
function DuplicateSuggestionsPanel({ treeId }) {
  const [state, setState] = useState({ loading: true, items: [] });
  useEffect(() => {
    http.get(`/persons-suggest-duplicates?tree_id=${treeId}&limit=25`)
      .then((r) => setState({ loading: false, items: r.data.items || [] }))
      .catch(() => setState({ loading: false, items: [] }));
  }, [treeId]);
  if (state.loading) {
    return <Panel title="Possible Duplicate Persons" icon={Users} testid="fmc-dup-panel">
      <div className="py-4 text-center opacity-60"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
    </Panel>;
  }
  return (
    <Panel title="Possible Duplicate Persons" icon={Users} testid="fmc-dup-panel">
      {state.items.length === 0 ? (
        <EmptyRow text="No duplicate suggestions — the auto-merger has this family covered." />
      ) : (
        <div className="space-y-3">
          <p className="text-xs opacity-70">
            AI thinks these pairs might be the same real person.  Compare their profiles and merge if they are.
          </p>
          {state.items.map((s, i) => (
            <div key={i} className="p-3 rounded-lg border" style={{ borderColor: "var(--wofy-border)" }} data-testid={`fmc-dup-pair-${i}`}>
              <div className="flex items-center gap-3">
                <DupPersonCard p={s.a} />
                <span className="text-xs opacity-60">vs</span>
                <DupPersonCard p={s.b} />
                <span className="text-xs font-mono ml-auto px-2 py-0.5 rounded shrink-0" style={{ background: "#FDF4FF", color: "#7C3AED" }}>
                  {s.score}% match
                </span>
              </div>
              <div className="mt-2 text-xs opacity-80">
                {s.reasons.join(" · ")}
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}


function DupPersonCard({ p }) {
  return (
    <Link
      to={`/person/${p.sp_id}`}
      className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80"
      data-testid={`fmc-dup-person-${p.sp_id}`}
    >
      {p.photo
        ? <img src={resolveImage(p.photo)} alt="" className="w-8 h-8 rounded-full object-cover" />
        : <div className="w-8 h-8 rounded-full" style={{ background: "#4338CA1A" }} />}
      <div className="min-w-0">
        <div className="text-sm font-semibold truncate">{[p.first_name, p.last_name].filter(Boolean).join(" ") || "Unnamed"}</div>
        <div className="text-[10px] font-mono opacity-60">{p.sp_id}</div>
      </div>
    </Link>
  );
}


// ---------------------------------------------------------------------------
// Memorial Records tab  (spec §13)
// ---------------------------------------------------------------------------
// Lists every deceased member of the tree and opens a comprehensive editor
// drawer that lets admins fill in the full §13 obituary + funeral record
// (biography, funeral / visitation / burial / cremation, military service,
// faith, favourite quotes, obituary text, eulogy, tributes, and media).
function MemorialTab({ treeId, canEdit }) {
  const [members, setMembers] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => {
    http.get(`/family-trees/${treeId}/members`)
      .then((r) => setMembers((r.data.items || []).filter((m) => (m.current_status || "").toLowerCase() === "deceased")))
      .catch(() => setMembers([]));
  };
  useEffect(load, [treeId]); // eslint-disable-line

  if (members === null) return <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto opacity-50" /></div>;

  if (members.length === 0) {
    return (
      <div className="wofy-card text-center py-12 opacity-80" data-testid="fmc-memorial-empty">
        <Flower2 className="w-10 h-10 mx-auto opacity-60 mb-2" style={{ color: "#7C3AED" }} />
        <p className="text-sm">No deceased members recorded in this tree.</p>
        <p className="text-xs mt-1 opacity-60">Add a member and set their status to "Deceased" to build a memorial record here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="fmc-memorial">
      <p className="text-sm opacity-70">
        Full obituary + funeral records for {members.length} deceased family member{members.length === 1 ? "" : "s"}.
        {" "}{canEdit ? "Tap any card to edit their memorial record." : "Read-only — you need an editor role to change these."}
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {members.map((m) => {
          const memo = m.memorial || {};
          const filled = Object.keys(memo).filter((k) => {
            const v = memo[k];
            return v && !(Array.isArray(v) && v.length === 0) && !(typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);
          }).length;
          return (
            <button
              key={m.id}
              onClick={() => setEditing(m)}
              className="text-left p-4 rounded-xl border hover:shadow-md transition"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid={`fmc-memorial-member-${m.id}`}
            >
              <div className="flex items-center gap-3">
                {m.photo
                  ? <img src={resolveImage(m.photo)} alt="" className="w-12 h-12 rounded-full object-cover border grayscale" />
                  : <div className="w-12 h-12 rounded-full" style={{ background: "#7C3AED1A" }} />}
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">
                    {[m.first_name, m.last_name].filter(Boolean).join(" ") || "Unnamed"}
                  </div>
                  <div className="text-xs opacity-70 truncate">
                    {m.birth_date ? m.birth_date.slice(0, 4) : "?"} – {m.death_date ? m.death_date.slice(0, 4) : "?"}
                  </div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: "#EEF2FF", color: "#4338CA" }}>
                  {filled === 0 ? "Not started" : `${filled} section${filled === 1 ? "" : "s"} filled`}
                </span>
                {m.memorial_slug && (
                  <Link
                    to={`/celebrate-a-life/${m.memorial_slug}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[10px] underline"
                    style={{ color: "#4338CA" }}
                  >
                    Public tribute
                  </Link>
                )}
              </div>
            </button>
          );
        })}
      </div>
      {editing && (
        <MemorialEditor
          treeId={treeId}
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={(next) => {
            setMembers((prev) => prev.map((m) => m.id === next.id ? next : m));
            setEditing(null);
          }}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}


// A single labelled text input used across the memorial editor.
function MField({ label, value, onChange, textarea, rows = 3, placeholder, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">{label}</div>
      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className="w-full px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid={testid}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid={testid}
        />
      )}
    </label>
  );
}


function MemorialEditor({ treeId, member, onClose, onSaved, canEdit }) {
  const initial = member.memorial || {};
  const [memo, setMemo] = useState({
    maiden_name: initial.maiden_name || "",
    cause_of_death: initial.cause_of_death || "",
    obituary: initial.obituary || "",
    eulogy: initial.eulogy || "",
    funeral: initial.funeral || { date: "", time: "", location: "", notes: "" },
    visitation: initial.visitation || { date: "", time: "", location: "", notes: "" },
    wake: initial.wake || { date: "", time: "", location: "", notes: "" },
    burial: initial.burial || { date: "", location: "", cemetery: "", grave_number: "" },
    cremation: initial.cremation || { date: "", crematorium: "", ashes_location: "" },
    military: initial.military || { branch: "", rank: "", service_years: "" },
    faith: initial.faith || { religion: "", denomination: "", place_of_worship: "" },
    favorite_quotes: (initial.favorite_quotes || []).join("\n"),
    livestream_url: initial.livestream_url || "",
    memorial_program_pdf: initial.memorial_program_pdf || "",
    newspaper_obituary_url: initial.newspaper_obituary_url || "",
    donations_note: (initial.donations || []).map((d) => `${d.charity_name || ""}|${d.url || ""}|${d.note || ""}`).join("\n"),
    memorial_anniversary_reminders_enabled: initial.memorial_anniversary_reminders_enabled ?? true,
  });
  const [busy, setBusy] = useState(false);

  const setNested = (group, key, value) =>
    setMemo((m) => ({ ...m, [group]: { ...(m[group] || {}), [key]: value } }));

  const save = async () => {
    setBusy(true);
    try {
      // Serialise back into the same shape MemberIn.memorial expects.
      const payload = {
        memorial: {
          ...memo,
          favorite_quotes: memo.favorite_quotes.split("\n").map((s) => s.trim()).filter(Boolean),
          donations: memo.donations_note.split("\n").map((line) => {
            const [charity_name, url, note] = line.split("|").map((s) => (s || "").trim());
            return charity_name ? { charity_name, url: url || "", note: note || "" } : null;
          }).filter(Boolean),
        },
      };
      delete payload.memorial.donations_note;
      const r = await http.patch(`/family-trees/${treeId}/members/${member.id}`, payload);
      toast.success("Memorial record saved");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save memorial");
    } finally {
      setBusy(false);
    }
  };

  const readonly = !canEdit;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="fmc-memorial-editor"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white border-b p-4 flex items-center gap-3" style={{ borderColor: "var(--wofy-border)" }}>
          {member.photo
            ? <img src={resolveImage(member.photo)} alt="" className="w-10 h-10 rounded-full object-cover grayscale" />
            : <div className="w-10 h-10 rounded-full" style={{ background: "#7C3AED1A" }} />}
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg truncate">
              In memory of {[member.first_name, member.last_name].filter(Boolean).join(" ")}
            </div>
            <div className="text-xs opacity-60">{member.birth_date} – {member.death_date}</div>
          </div>
          <button onClick={onClose} className="wofy-btn-ghost text-sm" data-testid="fmc-memorial-editor-close">Close</button>
          {!readonly && (
            <button onClick={save} disabled={busy} className="wofy-btn-primary text-sm flex items-center gap-1" data-testid="fmc-memorial-editor-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          )}
        </div>

        <div className="p-5 space-y-6" style={{ pointerEvents: readonly ? "none" : "auto", opacity: readonly ? 0.7 : 1 }}>
          <div>
            <h3 className="font-display text-base mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4" style={{ color: "#4338CA" }} /> Basic Information</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <MField label="Maiden name" value={memo.maiden_name} onChange={(v) => setMemo({ ...memo, maiden_name: v })} testid="mem-maiden" />
              <MField label="Cause of death (optional)" value={memo.cause_of_death} onChange={(v) => setMemo({ ...memo, cause_of_death: v })} testid="mem-cause" />
            </div>
          </div>

          <div>
            <h3 className="font-display text-base mb-3 flex items-center gap-2"><ScrollText className="w-4 h-4" style={{ color: "#4338CA" }} /> Obituary &amp; Eulogy</h3>
            <div className="space-y-3">
              <MField label="Obituary" textarea rows={5} value={memo.obituary} onChange={(v) => setMemo({ ...memo, obituary: v })} placeholder="Life story, achievements, family, community…" testid="mem-obit" />
              <MField label="Eulogy" textarea rows={4} value={memo.eulogy} onChange={(v) => setMemo({ ...memo, eulogy: v })} placeholder="Words delivered at the funeral…" testid="mem-eulogy" />
              <MField label="Favourite quotes (one per line)" textarea rows={3} value={memo.favorite_quotes} onChange={(v) => setMemo({ ...memo, favorite_quotes: v })} placeholder={"“Do small things with great love.”"} testid="mem-quotes" />
            </div>
          </div>

          <MemorialEventBlock label="Funeral"   icon={Cross}  data={memo.funeral}    onChange={(k, v) => setNested("funeral", k, v)}    testidPrefix="mem-funeral" />
          <MemorialEventBlock label="Visitation" data={memo.visitation} onChange={(k, v) => setNested("visitation", k, v)} testidPrefix="mem-visitation" />
          <MemorialEventBlock label="Wake"       data={memo.wake}       onChange={(k, v) => setNested("wake", k, v)}       testidPrefix="mem-wake" />

          <div>
            <h3 className="font-display text-base mb-3 flex items-center gap-2"><Flower2 className="w-4 h-4" style={{ color: "#4338CA" }} /> Burial</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <MField label="Burial date"    value={memo.burial.date}         onChange={(v) => setNested("burial", "date", v)} placeholder="2025-06-10" testid="mem-burial-date" />
              <MField label="Cemetery"       value={memo.burial.cemetery}     onChange={(v) => setNested("burial", "cemetery", v)} testid="mem-burial-cemetery" />
              <MField label="Location"       value={memo.burial.location}     onChange={(v) => setNested("burial", "location", v)} testid="mem-burial-location" />
              <MField label="Grave / plot #" value={memo.burial.grave_number} onChange={(v) => setNested("burial", "grave_number", v)} testid="mem-burial-grave" />
            </div>
          </div>

          <div>
            <h3 className="font-display text-base mb-3 flex items-center gap-2">🔥 Cremation</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <MField label="Cremation date"  value={memo.cremation.date}           onChange={(v) => setNested("cremation", "date", v)} placeholder="2025-06-10" testid="mem-crem-date" />
              <MField label="Crematorium"     value={memo.cremation.crematorium}    onChange={(v) => setNested("cremation", "crematorium", v)} testid="mem-crem-place" />
              <MField label="Ashes location"  value={memo.cremation.ashes_location} onChange={(v) => setNested("cremation", "ashes_location", v)} testid="mem-crem-ashes" />
            </div>
          </div>

          <div>
            <h3 className="font-display text-base mb-3 flex items-center gap-2">🎖️ Military Service</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <MField label="Branch"        value={memo.military.branch}        onChange={(v) => setNested("military", "branch", v)} testid="mem-mil-branch" />
              <MField label="Rank"          value={memo.military.rank}          onChange={(v) => setNested("military", "rank", v)} testid="mem-mil-rank" />
              <MField label="Service years" value={memo.military.service_years} onChange={(v) => setNested("military", "service_years", v)} placeholder="1968–1972" testid="mem-mil-years" />
            </div>
          </div>

          <div>
            <h3 className="font-display text-base mb-3 flex items-center gap-2"><Church className="w-4 h-4" style={{ color: "#4338CA" }} /> Faith &amp; Spiritual Life</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <MField label="Religion"           value={memo.faith.religion}          onChange={(v) => setNested("faith", "religion", v)} testid="mem-faith-religion" />
              <MField label="Denomination"       value={memo.faith.denomination}      onChange={(v) => setNested("faith", "denomination", v)} testid="mem-faith-denom" />
              <MField label="Place of worship"   value={memo.faith.place_of_worship}  onChange={(v) => setNested("faith", "place_of_worship", v)} testid="mem-faith-place" />
            </div>
          </div>

          <div>
            <h3 className="font-display text-base mb-3">Media &amp; Links</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <MField label="Livestream recording URL" value={memo.livestream_url}         onChange={(v) => setMemo({ ...memo, livestream_url: v })} placeholder="https://…" testid="mem-livestream" />
              <MField label="Memorial program (PDF)"   value={memo.memorial_program_pdf}   onChange={(v) => setMemo({ ...memo, memorial_program_pdf: v })} placeholder="https://…" testid="mem-program" />
              <MField label="Newspaper obituary URL"   value={memo.newspaper_obituary_url} onChange={(v) => setMemo({ ...memo, newspaper_obituary_url: v })} placeholder="https://…" testid="mem-newspaper" />
            </div>
          </div>

          <div>
            <h3 className="font-display text-base mb-3">Donations in Memory</h3>
            <MField
              label="One donation per line — format: Charity | URL | Note"
              textarea rows={3}
              value={memo.donations_note}
              onChange={(v) => setMemo({ ...memo, donations_note: v })}
              placeholder={"American Cancer Society | https://cancer.org | In memoriam of Mom\nLocal Food Bank |  | "}
              testid="mem-donations"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!memo.memorial_anniversary_reminders_enabled}
              onChange={(e) => setMemo({ ...memo, memorial_anniversary_reminders_enabled: e.target.checked })}
              data-testid="mem-reminders-toggle"
            />
            Send memorial anniversary reminders to family members
          </label>
        </div>
      </div>
    </div>
  );
}


function MemorialEventBlock({ label, icon: Icon, data, onChange, testidPrefix }) {
  return (
    <div>
      <h3 className="font-display text-base mb-3 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" style={{ color: "#4338CA" }} />}
        {label}
      </h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <MField label="Date"     value={data.date}     onChange={(v) => onChange("date", v)}     placeholder="2025-06-10" testid={`${testidPrefix}-date`} />
        <MField label="Time"     value={data.time}     onChange={(v) => onChange("time", v)}     placeholder="14:30" testid={`${testidPrefix}-time`} />
        <MField label="Location" value={data.location} onChange={(v) => onChange("location", v)} testid={`${testidPrefix}-location`} />
        <MField label="Notes"    value={data.notes}    onChange={(v) => onChange("notes", v)}    testid={`${testidPrefix}-notes`} />
      </div>
    </div>
  );
}


export default function FamilyManagementCenter() {
  const { treeId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tree, setTree] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTabState] = useState(() => {
    const q = searchParams.get("tab");
    return (TABS.find((t) => t.key === q) ? q : "dashboard");
  });
  const setTab = (key) => {
    setTabState(key);
    const next = new URLSearchParams(searchParams);
    if (key === "dashboard") next.delete("tab"); else next.set("tab", key);
    setSearchParams(next, { replace: true });
  };
  const [unreadContacts, setUnreadContacts] = useState(0);

  useEffect(() => {
    setLoading(true);
    http.get(`/family-trees/${treeId}`)
      .then(async (r) => {
        setTree(r.data);
        try {
          const d = await http.get(`/family-trees/${r.data.id}/dashboard`);
          setDashboard(d.data);
        } catch { /* dashboard is best-effort */ }
      })
      .catch(() => setTree(null))
      .finally(() => setLoading(false));
  }, [treeId]);

  const isAdmin = tree && user && (tree.owner_id === user.id || (tree.admin_ids || []).includes(user.id) || user.is_admin);

  // Poll unread contact-inbox count for the bell badge (admin only)
  useEffect(() => {
    if (!tree || !isAdmin) return;
    let alive = true;
    const load = () => {
      http.get(`/family-trees/${tree.id}/contacts/inbox`, { params: { status: "open" } })
        .then((r) => alive && setUnreadContacts(r.data?.counts?.open || 0))
        .catch(() => alive && setUnreadContacts(0));
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [tree, isAdmin]);

  if (loading) return <div className="py-24 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!tree) return <div className="max-w-2xl mx-auto py-16 px-6 text-center">
    <p className="opacity-70">This family tree isn't available.</p>
    <Link to="/family" className="wofy-btn-primary mt-4">Back to family trees</Link>
  </div>;

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6" data-testid="family-management-center">
      <button
        onClick={() => nav(`/family/${tree.id}`)}
        className="text-sm flex items-center gap-1 opacity-70 hover:opacity-100 mb-4"
        data-testid="fmc-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back to {tree.name}
      </button>

      {/* Page title */}
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl leading-tight" data-testid="fmc-title">
          Family Management Center
        </h1>
        <div className="mt-1 text-sm sm:text-base font-medium" style={{ color: "#4338CA" }} data-testid="fmc-subtitle">
          Family Reunion · <span className="italic">Kudumba Yogam</span>
        </div>
      </div>

      <div className="mb-6 border-b flex items-center gap-1 overflow-x-auto" style={{ borderColor: "var(--wofy-border)" }}>
        {TABS.filter((t) => !t.adminOnly || isAdmin).map((t) => {
          const active = tab === t.key;
          const showBadge = t.showBadge && unreadContacts > 0;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className="px-4 py-2.5 text-sm flex items-center gap-2 border-b-2 whitespace-nowrap transition relative"
              style={active
                ? { borderColor: "#4338CA", color: "#4338CA", fontWeight: 600 }
                : { borderColor: "transparent", color: "var(--wofy-text-soft)" }}
              data-testid={`fmc-tab-${t.key}`}
            >
              <t.Icon className="w-4 h-4" /> {t.label}
              {showBadge && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="fmc-messages-badge">
                  {unreadContacts > 99 ? "99+" : unreadContacts}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "dashboard"   && <DashboardTab tree={tree} data={dashboard} loading={loading} />}
      {tab === "members"     && <MembersTab treeId={tree.id} />}
      {tab === "documents"   && <DocumentsTab tree={tree} isAdmin={!!isAdmin} />}
      {tab === "photos"      && <FamilyPhotosTab treeId={tree.id} isAdmin={!!isAdmin} />}
      {tab === "meetings"    && <MeetingMinutesTab treeId={tree.id} isAdmin={!!isAdmin} />}
      {tab === "charity"     && <CharityTab treeId={tree.id} isAdmin={!!isAdmin} />}
      {tab === "notes"       && <CommentsNotesTab treeId={tree.id} isAdmin={!!isAdmin} />}
      {tab === "discoveries" && <DiscoveriesTab treeId={tree.id} />}
      {tab === "messages"    && <MessagesTab treeId={tree.id} isAdmin={!!isAdmin} onCountChange={setUnreadContacts} />}
      {tab === "memorial"    && <MemorialTab treeId={tree.id} canEdit={!!isAdmin} />}
      {tab === "calendar"    && <CalendarTab treeId={tree.id} />}
      {tab === "roles"       && isAdmin && <RolesTab tree={tree} treeId={tree.id} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*                         DISCOVERIES TAB                            */
/* ------------------------------------------------------------------ */
/** AncestorMatchesPanel — inline dashboard listing pending
 *  ancestor auto-match suggestions for this tree. Side-by-side
 *  compare + one-click resolve/dismiss. Powered by the
 *  /api/ancestor-matches endpoint set from routes/ancestor_matches.py. */
function AncestorMatchesPanel({ treeId }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState({});
  const [mergeFor, setMergeFor] = useState(null); // match row when the wizard is open

  const load = () => {
    http.get(`/ancestor-matches`, { params: { tree_id: treeId, status: "open", limit: 50 } })
      .then((r) => setRows(r.data?.items || []))
      .catch(() => setRows([]));
  };
  useEffect(load, [treeId]); // eslint-disable-line

  const dismiss = async (id) => {
    setBusy((b) => ({ ...b, [id]: "dismiss" }));
    try {
      await http.post(`/ancestor-matches/${id}/dismiss`);
      toast.success("Match dismissed");
      setRows((rs) => (rs || []).filter((r) => r.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not dismiss the match");
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  const openMerge = (m) => setMergeFor(m);
  const onMerged = (matchId) => {
    setMergeFor(null);
    setRows((rs) => (rs || []).filter((r) => r.id !== matchId));
  };

  if (rows === null) {
    return (
      <div className="wofy-card py-6 text-center opacity-70" data-testid="fmc-ancestor-matches-loading">
        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
      </div>
    );
  }
  if (rows.length === 0) return null; // hide when empty — Discoveries has its own empty state

  return (
    <section data-testid="fmc-ancestor-matches">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-mono uppercase tracking-wider opacity-70">
          Ancestor auto-matches
        </div>
        <span className="text-[11px] font-mono opacity-60">
          {rows.length} pending
        </span>
      </div>
      <div className="grid gap-3">
        {rows.map((m) => {
          const isThisLeft = m.left_tree === treeId;
          const mine  = isThisLeft ? { name: m.left_name,  year: m.left_year,  tree: m.left_tree,  member: m.left_member  }
                                    : { name: m.right_name, year: m.right_year, tree: m.right_tree, member: m.right_member };
          const other = isThisLeft ? { name: m.right_name, year: m.right_year, tree: m.right_tree, member: m.right_member }
                                    : { name: m.left_name,  year: m.left_year,  tree: m.left_tree,  member: m.left_member  };
          const strength = m.strength || "likely";
          return (
            <div key={m.id} className="wofy-card" data-testid={`ancestor-match-${m.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full"
                  style={{
                    background: strength === "exact"  ? "#FEF3C7"
                             :  strength === "strong" ? "#DCFCE7" : "#EEF2FF",
                    color:      strength === "exact"  ? "#92400E"
                             :  strength === "strong" ? "#166534" : "#3730A3",
                  }}
                  data-testid={`ancestor-match-strength-${m.id}`}
                >
                  {strength === "exact" ? "Exact match" : strength === "strong" ? "Strong match" : "Possible match"}
                </span>
                {strength === "exact" && (
                  <span className="text-[11px] opacity-70">
                    Same name, family, and birth date — link the trees?
                  </span>
                )}
              </div>
              <div className="grid md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono uppercase opacity-60 mb-0.5">Your tree</div>
                  <Link
                    to={`/family/${mine.tree}/member/${mine.member}`}
                    className="font-medium truncate hover:underline"
                    data-testid={`ancestor-match-mine-${m.id}`}
                  >
                    {mine.name}
                  </Link>
                  <div className="text-[11px] font-mono opacity-70">
                    {mine.year ? `b. ${mine.year}` : "no birth year"}
                  </div>
                </div>
                <div className="text-center opacity-60 text-xs">
                  <div className="whitespace-nowrap">↔ possibly the same</div>
                  {m.shared_places && m.shared_places.length > 0 && (
                    <div className="text-[10px] mt-0.5">
                      shared: {m.shared_places.slice(0, 3).join(", ")}
                    </div>
                  )}
                </div>
                <div className="min-w-0 md:text-right">
                  <div className="text-[10px] font-mono uppercase opacity-60 mb-0.5">Other tree</div>
                  <Link
                    to={`/family/${other.tree}/member/${other.member}`}
                    className="font-medium truncate hover:underline"
                    data-testid={`ancestor-match-other-${m.id}`}
                  >
                    {other.name}
                  </Link>
                  <div className="text-[11px] font-mono opacity-70">
                    {other.year ? `b. ${other.year}` : "no birth year"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 justify-end mt-3 pt-3 border-t" style={{ borderColor: "var(--wofy-border)" }}>
                <button
                  onClick={() => dismiss(m.id)}
                  disabled={!!busy[m.id]}
                  className="wofy-btn-ghost text-xs"
                  data-testid={`ancestor-match-dismiss-${m.id}`}
                >
                  {busy[m.id] === "dismiss" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Not a match"}
                </button>
                <button
                  onClick={() => openMerge(m)}
                  disabled={!!busy[m.id]}
                  className="wofy-btn-primary text-xs"
                  data-testid={`ancestor-match-resolve-${m.id}`}
                >
                  Confirm match…
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {mergeFor && (
        <MergeWizardModal
          match={mergeFor}
          treeId={treeId}
          onClose={() => setMergeFor(null)}
          onMerged={onMerged}
        />
      )}
    </section>
  );
}


/** MergeWizardModal — pick which of the two members should be the
 *  SURVIVING record, which content to copy from the other side
 *  (photos, stories, AI portraits, relationships), then fire the
 *  merge. On success, notifies parent to remove the row. */
function MergeWizardModal({ match, treeId, onClose, onMerged }) {
  const [survivorSide, setSurvivorSide] = useState(
    match.left_tree === treeId ? "left" : "right"
  );
  const [copyPhotos, setCopyPhotos] = useState(true);
  const [copyStories, setCopyStories] = useState(true);
  const [copyPortraits, setCopyPortraits] = useState(true);
  const [copyRels, setCopyRels] = useState(false);
  const [saving, setSaving] = useState(false);

  const leftSelected  = survivorSide === "left";
  const rightSelected = survivorSide === "right";

  const submit = async () => {
    setSaving(true);
    try {
      const res = await http.post(`/ancestor-matches/${match.id}/merge`, {
        into_side: survivorSide,
        copy_photos: copyPhotos,
        copy_stories: copyStories,
        copy_ai_portraits: copyPortraits,
        copy_relationships: copyRels,
      });
      const c = res.data?.copied || {};
      toast.success(
        `Merged — copied ${c.photos || 0} photos, ${c.stories || 0} stories, ${c.portraits || 0} portraits${
          c.relationships ? `, ${c.relationships} relationships` : ""
        }.`
      );
      onMerged(match.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
      data-testid="ancestor-merge-modal"
    >
      <div
        className="wofy-card max-w-lg w-full max-h-[90vh] overflow-y-auto"
        style={{ background: "var(--wofy-bg)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3">
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-60">Merge Wizard</div>
          <h3 className="font-display text-xl">Confirm match &amp; copy content</h3>
          <p className="text-xs opacity-70 mt-1">
            Choose which record survives. Content you tick will be copied
            from the other member into it. The other record is soft-marked
            as merged and hidden but never deleted.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          <button
            onClick={() => setSurvivorSide("left")}
            className={"rounded-lg border p-3 text-left transition"}
            style={{
              borderColor: leftSelected ? "#1D4ED8" : "var(--wofy-border)",
              background:  leftSelected ? "#EFF6FF" : "transparent",
            }}
            data-testid="merge-pick-left"
          >
            <div className="text-[10px] font-mono uppercase opacity-60">Keep this</div>
            <div className="font-semibold truncate">{match.left_name}</div>
            <div className="text-[11px] font-mono opacity-70">
              {match.left_year ? `b. ${match.left_year}` : "no birth year"}
            </div>
          </button>
          <button
            onClick={() => setSurvivorSide("right")}
            className={"rounded-lg border p-3 text-left transition"}
            style={{
              borderColor: rightSelected ? "#1D4ED8" : "var(--wofy-border)",
              background:  rightSelected ? "#EFF6FF" : "transparent",
            }}
            data-testid="merge-pick-right"
          >
            <div className="text-[10px] font-mono uppercase opacity-60">Keep this</div>
            <div className="font-semibold truncate">{match.right_name}</div>
            <div className="text-[11px] font-mono opacity-70">
              {match.right_year ? `b. ${match.right_year}` : "no birth year"}
            </div>
          </button>
        </div>

        <div className="mt-4 space-y-2">
          <div className="text-[11px] font-mono uppercase opacity-60">Copy from the other record</div>
          <MergeCheckbox testid="merge-copy-photos"    checked={copyPhotos}    onChange={setCopyPhotos}    label="Photos" hint="All photo attachments, plus set primary if missing" />
          <MergeCheckbox testid="merge-copy-stories"   checked={copyStories}   onChange={setCopyStories}   label="Biography &amp; stories" hint="Append bio + copy standalone story documents" />
          <MergeCheckbox testid="merge-copy-portraits" checked={copyPortraits} onChange={setCopyPortraits} label="AI portraits" hint="Every AI-generated ancestry portrait" />
          <MergeCheckbox testid="merge-copy-rels"      checked={copyRels}      onChange={setCopyRels}      label="Relationships"  hint="Retarget the other member's family edges — advanced" />
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="wofy-btn-ghost text-sm"
            disabled={saving}
            data-testid="merge-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="wofy-btn-primary text-sm"
            data-testid="merge-submit"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Merge records"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MergeCheckbox({ testid, checked, onChange, label, hint }) {
  return (
    <label
      className="flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer transition"
      style={{
        borderColor: checked ? "#1D4ED8" : "var(--wofy-border)",
        background:  checked ? "#EFF6FF" : "transparent",
      }}
      data-testid={testid}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
      />
      <span className="min-w-0">
        <span className="block text-sm font-semibold" dangerouslySetInnerHTML={{ __html: label }} />
        <span className="block text-[11px] opacity-60">{hint}</span>
      </span>
    </label>
  );
}


function DiscoveriesTab({ treeId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    http.get(`/family-trees/${treeId}/discoveries`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData({}))
      .finally(() => alive && setLoading(false));
  }, [treeId]);

  if (loading) return <div className="py-12 text-center opacity-70"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  const newMembers = data?.new_members || [];
  const newRelationships = data?.new_relationships || [];
  const connections = data?.new_connections || [];
  const suggestions = data?.duplicate_hints || [];

  return (
    <div className="space-y-6" data-testid="fmc-discoveries">
      <div>
        <h2 className="font-display text-2xl">Discoveries</h2>
        <p className="text-sm opacity-70 mt-1">
          Newly added members, freshly minted relationships, and cross-family
          connections detected from the global network.
        </p>
      </div>

      <AncestorMatchesPanel treeId={treeId} />

      {!newMembers.length && !newRelationships.length && !connections.length && !suggestions.length && (
        <div className="wofy-card text-center py-10">
          <Sparkles className="w-6 h-6 mx-auto mb-2 opacity-60" />
          <p className="text-sm opacity-70">No new discoveries yet. Add or link a member and we'll surface connections here.</p>
        </div>
      )}

      {newMembers.length > 0 && (
        <section>
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">New members (last 30 days)</div>
          <div className="grid md:grid-cols-2 gap-3">
            {newMembers.map((m) => (
              <Link key={m.id} to={`/family/${treeId}/member/${m.id}`} className="wofy-card hover:bg-black/5" data-testid={`disc-new-member-${m.id}`}>
                <div className="flex items-center gap-3">
                  {m.portrait ? <img src={resolveImage(m.portrait)} className="w-10 h-10 rounded-full object-cover" alt="" /> : <div className="w-10 h-10 rounded-full bg-black/10" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{[m.first_name, m.last_name].filter(Boolean).join(" ")}</div>
                    <div className="text-[11px] font-mono opacity-70 truncate">{m.role || "member"} · added {m.created_at ? new Date(m.created_at).toLocaleDateString() : ""}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {newRelationships.length > 0 && (
        <section>
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">New relationships</div>
          <ul className="wofy-card space-y-2">
            {newRelationships.map((r) => (
              <li key={r.edge_id} className="text-sm flex items-center gap-2" data-testid={`disc-new-rel-${r.edge_id}`}>
                <Heart className="w-3 h-3 opacity-60" />
                <span><b>{r.a_name}</b> — {r.kind.replace(/_/g, " ")} — <b>{r.b_name}</b></span>
                <span className="ml-auto text-[10px] font-mono opacity-60">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {connections.length > 0 && (
        <section>
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">Connections to other family trees</div>
          <div className="grid md:grid-cols-2 gap-3">
            {connections.map((c, i) => (
              <div key={i} className="wofy-card" data-testid={`disc-connection-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.other_tree?.name || "Another family"}</div>
                    <div className="text-[11px] font-mono opacity-70 truncate">via {c.kind?.replace(/_/g, " ") || "marriage"} · {c.verification || "pending"}</div>
                  </div>
                  {c.other_tree?.id && (
                    <Link to={`/family/${c.other_tree.id}`} className="wofy-btn-ghost text-xs">
                      <ExternalLink className="w-3 h-3" /> Visit
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {suggestions.length > 0 && (
        <section>
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-2">Possible duplicates / merge hints</div>
          <div className="grid md:grid-cols-2 gap-3">
            {suggestions.map((s, i) => (
              <div key={i} className="wofy-card" data-testid={`disc-suggestion-${i}`}>
                <div className="font-medium">{s.a_name} ↔ {s.b_name}</div>
                <div className="text-[11px] font-mono opacity-70">Score: {s.score} · {s.reason || "similar name/dob"}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/*                         MESSAGES TAB                               */
/* ------------------------------------------------------------------ */
const STATUS_LABELS = {
  open: "Open",
  seen: "Seen",
  in_progress: "In Progress",
  fixed: "Fixed",
  other: "Other",
  closed: "Closed",
};
const STATUS_COLORS = {
  open: "bg-amber-100 text-amber-800",
  seen: "bg-blue-100 text-blue-800",
  in_progress: "bg-indigo-100 text-indigo-800",
  fixed: "bg-emerald-100 text-emerald-800",
  other: "bg-gray-100 text-gray-800",
  closed: "bg-gray-50 text-gray-500 line-through",
};
const PURPOSE_LABELS = {
  claim_edit: "Claim edit rights",
  submit_photographs: "Submit photos",
  upload_document: "Upload document",
  found_mistake: "Found a mistake",
  other: "Other",
};

function MessagesTab({ treeId, isAdmin, onCountChange }) {
  const [data, setData] = useState({ items: [], counts: {} });
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    http.get(`/family-trees/${treeId}/contacts/inbox`, { params: { status } })
      .then((r) => {
        setData(r.data);
        onCountChange && onCountChange(r.data?.counts?.open || 0);
      })
      .catch(() => setData({ items: [], counts: {} }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [treeId, status]);

  if (!isAdmin) {
    return (
      <div className="wofy-card text-center py-10">
        <ShieldCheck className="w-6 h-6 mx-auto mb-2 opacity-60" />
        <p className="text-sm opacity-70">The messages inbox is visible to tree owners and admins only.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="fmc-messages">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl">Messages inbox</h2>
          <p className="text-sm opacity-70 mt-1">Reach-outs from visitors — claims, photo submissions, corrections. Every state change is audit-logged.</p>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {["all", ...Object.keys(STATUS_LABELS)].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`text-xs px-3 py-1.5 rounded-full border ${status === s ? "bg-black text-white border-black" : "hover:bg-black/5"}`}
              style={status !== s ? { borderColor: "var(--wofy-border)" } : undefined}
              data-testid={`fmc-msg-filter-${s}`}
            >
              {s === "all" ? "All" : STATUS_LABELS[s]}
              {" "}({data.counts?.[s] ?? (s === "all" ? data.counts?.all : 0) ?? 0})
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto opacity-70" /></div>
      ) : data.items.length === 0 ? (
        <div className="wofy-card text-center py-10">
          <Inbox className="w-6 h-6 mx-auto mb-2 opacity-60" />
          <p className="text-sm opacity-70">Inbox is clear.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-[1fr_1.2fr] gap-4">
          {/* List */}
          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {data.items.map((c) => {
              const active = selected?.id === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setSelected(c)}
                  className={`w-full text-left wofy-card ${active ? "ring-2 ring-black" : ""}`}
                  data-testid={`fmc-msg-item-${c.id}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status] || "bg-black/5"}`}>
                      {STATUS_LABELS[c.status] || c.status}
                    </span>
                    <span className="text-[10px] font-mono opacity-60">{new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="text-sm font-medium truncate">{c.member_name}</div>
                  <div className="text-[11px] opacity-70 truncate">
                    {PURPOSE_LABELS[c.purpose] || c.purpose} · from {c.sender_name}
                  </div>
                  {(c.photos?.length || c.documents?.length) && (
                    <div className="mt-1 text-[10px] font-mono opacity-70">
                      <Paperclip className="w-3 h-3 inline" /> {(c.photos?.length || 0) + (c.documents?.length || 0)} attachment(s)
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Detail */}
          <div className="wofy-card">
            {selected ? (
              <MessageDetail
                key={selected.id}
                message={selected}
                onUpdated={(next) => {
                  setSelected(next);
                  setData((d) => ({ ...d, items: d.items.map((it) => it.id === next.id ? next : it) }));
                  load();
                }}
              />
            ) : (
              <div className="text-center py-16 opacity-70 text-sm">Select a message to view and respond.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MessageDetail({ message, onUpdated }) {
  const [reply, setReply] = useState("");
  const [nextStatus, setNextStatus] = useState(message.status || "seen");
  const [busy, setBusy] = useState(false);

  const respond = async () => {
    setBusy(true);
    try {
      const { data } = await http.patch(`/family-tree-contacts/${message.id}`, { status: nextStatus, reply: reply || undefined });
      toast.success(`Marked ${STATUS_LABELS[nextStatus]}`);
      onUpdated && onUpdated(data);
      setReply("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid={`fmc-msg-detail-${message.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">About: {message.member_name}</div>
          <div className="text-[11px] font-mono opacity-70">Purpose: {PURPOSE_LABELS[message.purpose] || message.purpose}</div>
        </div>
        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLORS[message.status] || "bg-black/5"}`}>
          {STATUS_LABELS[message.status] || message.status}
        </span>
      </div>

      <div className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }}>
        <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">
          {message.sender_name} · {new Date(message.created_at).toLocaleString()}
        </div>
        {message.subject && <div className="font-medium mb-1">{message.subject}</div>}
        <p className="whitespace-pre-wrap text-sm">{message.body}</p>
      </div>

      {message.photos?.length > 0 && (
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Photos ({message.photos.length})</div>
          <div className="grid grid-cols-3 gap-2">
            {message.photos.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noreferrer" className="block">
                <img src={p.url} alt="" className="w-full h-24 object-cover rounded" />
                <div className="text-[10px] opacity-70 mt-1 truncate">{p.caption}</div>
              </a>
            ))}
          </div>
        </div>
      )}

      {message.documents?.length > 0 && (
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Documents ({message.documents.length})</div>
          <div className="space-y-1">
            {message.documents.map((d, i) => (
              <a key={i} href={d.url} target="_blank" rel="noreferrer" className="text-sm inline-flex items-center gap-2 opacity-90 hover:opacity-100">
                <Paperclip className="w-3 h-3" /> {d.filename || `Document ${i + 1}`} — {d.caption}
              </a>
            ))}
          </div>
        </div>
      )}

      {message.response_body && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
          <div className="text-[11px] font-mono uppercase tracking-wider opacity-70 mb-1">Previous reply</div>
          <div className="whitespace-pre-wrap text-sm">{message.response_body}</div>
        </div>
      )}

      {/* Respond block */}
      <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: "var(--wofy-border)" }}>
        <div className="text-[11px] font-mono uppercase tracking-wider">Respond</div>
        <textarea
          rows={3}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Optional reply message…"
          className="w-full rounded border px-3 py-2 text-sm bg-transparent"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="fmc-msg-reply"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <select value={nextStatus} onChange={(e) => setNextStatus(e.target.value)} className="rounded border px-2 py-1.5 text-sm bg-transparent" style={{ borderColor: "var(--wofy-border)" }} data-testid="fmc-msg-next-status">
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>Mark as {v}</option>)}
          </select>
          <button disabled={busy} onClick={respond} className="wofy-btn-primary text-sm" data-testid="fmc-msg-respond">
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : <><CheckCircle2 className="w-3 h-3" /> Update</>}
          </button>
        </div>
      </div>

      {/* Audit trail */}
      {message.audit_trail?.length > 0 && (
        <details>
          <summary className="text-[11px] font-mono uppercase tracking-wider cursor-pointer opacity-70">
            <Clock className="w-3 h-3 inline mr-1" />
            Audit trail ({message.audit_trail.length})
          </summary>
          <ul className="mt-2 space-y-1 text-[11px] opacity-80">
            {message.audit_trail.map((a, i) => (
              <li key={i}>· {new Date(a.at).toLocaleString()} — <b>{a.action}</b> by {a.by_name || "system"}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}



/* ================================================================
 * Documents Tab — long-form family PDFs (books, memoirs, records)
 * rendered via /pages/FamilyBookViewer for the flipbook experience.
 * ================================================================ */
function DocumentsTab({ tree, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("members");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/family-trees/${tree.slug || tree.id}/documents`);
      setItems(data.items || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [tree.slug, tree.id]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Please choose a PDF first."); return; }
    if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("Only PDF files are supported."); return; }
    if (f.size > 120 * 1024 * 1024) { toast.error("PDF must be < 120 MB."); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("title", title.trim());
    fd.append("description", description.trim());
    fd.append("visibility", visibility);
    setUploading(true);
    try {
      await http.post(`/family-trees/${tree.slug || tree.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded — page rasterisation is running in the background. Refresh in a minute.");
      setTitle(""); setDescription(""); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const remove = async (docId) => {
    if (!window.confirm("Delete this document? Its rasterised pages will be soft-deleted.")) return;
    try {
      await http.delete(`/family-trees/${tree.slug || tree.id}/documents/${docId}`);
      toast.success("Document deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="fmc-documents-tab">
      <div>
        <div className="overline text-xs mb-1">DOCUMENTS · Family books, memoirs & records</div>
        <p className="text-sm opacity-70">
          Preserve heirloom PDFs — every page is rasterised into a real page-turn book that
          descendants can flip through on any device.
        </p>
      </div>

      {isAdmin && (
        <Panel title="Upload a new document" icon={BookOpen} testid="fmc-doc-upload">
          <div className="space-y-3">
            <input
              type="file"
              accept="application/pdf"
              ref={fileRef}
              className="block w-full text-sm"
              data-testid="fmc-doc-file"
            />
            <div className="grid sm:grid-cols-2 gap-2">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Title (e.g. Vellaringattu Family Book)"
                className="px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="fmc-doc-title"
              />
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                className="px-3 py-2 rounded-lg border text-sm bg-white"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="fmc-doc-visibility"
              >
                <option value="public">Public — anyone can read</option>
                <option value="members">Tree members only</option>
                <option value="admins">Admins & editors only</option>
              </select>
            </div>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A short description (optional)"
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="fmc-doc-description"
            />
            <button
              type="button"
              onClick={upload}
              disabled={uploading}
              className="wofy-btn-primary text-sm"
              data-testid="fmc-doc-upload-submit"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
              {uploading ? "Uploading…" : "Upload PDF"}
            </button>
            <div className="text-xs opacity-60">
              Large PDFs may take a minute to rasterise. Come back — page thumbnails will appear once ready.
            </div>
          </div>
        </Panel>
      )}

      <div>
        <div className="text-xs font-mono uppercase opacity-70 mb-2">
          {loading ? "Loading…" : `${items.length} document${items.length === 1 ? "" : "s"}`}
        </div>
        {(!loading && items.length === 0) && (
          <div className="wofy-card text-center text-sm" style={{ color: "var(--wofy-text-soft)" }} data-testid="fmc-doc-empty">
            No documents yet. {isAdmin ? "Upload a PDF above to add one." : "Ask an admin to upload one."}
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((d) => (
            <div
              key={d.id}
              className="wofy-card overflow-hidden"
              data-testid={`fmc-doc-card-${d.id}`}
            >
              <div className="aspect-[3/4] bg-gradient-to-br from-[#F5EFE1] to-[#E6D6A8] flex items-center justify-center overflow-hidden">
                <BookOpen className="w-16 h-16 opacity-30" />
              </div>
              <div className="mt-3">
                <div className="font-semibold text-sm">{d.title}</div>
                {d.description && (
                  <div className="text-xs mt-1 opacity-70 line-clamp-2">{d.description}</div>
                )}
                <div className="text-[10px] font-mono uppercase mt-2 opacity-60">
                  {d.status === "ready"
                    ? <>Ready · {d.page_count} pages</>
                    : d.status === "processing"
                      ? <>Rasterising… {d.page_count || 0} pages so far</>
                      : d.status === "failed"
                        ? <>Failed — try re-uploading</>
                        : d.status}
                  {" · "}
                  {d.visibility}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  {d.status === "ready" || (d.page_count && d.page_count > 0) ? (
                    <Link
                      to={`/family/${tree.slug || tree.id}/book/${d.id}`}
                      className="wofy-btn-primary text-xs"
                      data-testid={`fmc-doc-open-${d.id}`}
                    >
                      <BookOpen className="w-3 h-3" /> Open book
                    </Link>
                  ) : (
                    <button disabled className="wofy-btn-ghost text-xs opacity-50">
                      <Loader2 className="w-3 h-3 animate-spin" /> Processing…
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => remove(d.id)}
                      className="wofy-btn-ghost text-xs"
                      data-testid={`fmc-doc-delete-${d.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

